import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ref, listAll, getMetadata } from "firebase/storage";
import Container from "react-bootstrap/Container";
import { storage } from "../firebase";
import { selectUser } from "../features/user/userSlice";
import { useSelector } from "react-redux";
import LoadingSpinner from "../components/LoadingSpinner";
import { FaArrowRight } from "react-icons/fa6";
import "../css/menu-cards.css";
import { useI18n } from "../v2/i18n/I18nProvider";

const titleFromFilename = (filename) =>
  filename.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Cache en memoria de la lista de menús del usuario: volver a "mis menús" no
// re-lista Storage ni muestra el spinner full-screen (se sentía como un reload).
let menusCache = null; // { uid, data }

const Profile = () => {
  const user = useSelector(selectUser);
  const cached = menusCache && menusCache.uid === user?.uid ? menusCache.data : null;
  const [userMenus, setUserMenus] = useState(cached);
  const [isLoading, setIsLoading] = useState(!cached);
  const { t, locale } = useI18n();

  useEffect(() => {
    if (!user?.uid) return;
    let isMounted = true;

    const listFolder = async (folder, version, editorPathBuilder) => {
      try {
        const folderRef = ref(storage, `${folder}/${user.uid}`);
        const { items } = await listAll(folderRef);
        const jsonFiles = items.filter((i) => i.name.endsWith(".json"));

        const metas = await Promise.all(
          jsonFiles.map(async (file) => {
            const name = file.name.replace(".json", "");
            let updated = null;
            try {
              const meta = await getMetadata(file);
              updated = meta.updated || meta.timeCreated || null;
            } catch (_) {}
            return {
              name,
              version,
              editorUrl: editorPathBuilder(name),
              dashboardUrl: `/profile/${user.uid}/${name}/panel`,
              title: titleFromFilename(name),
              updated,
            };
          })
        );
        return metas;
      } catch (err) {
        if (err?.code !== "storage/object-not-found") {
          console.warn(`No pude listar ${folder}:`, err);
        }
        return [];
      }
    };

    const fetchUserMenus = async () => {
      // Con cache mostramos la lista al instante y refrescamos en SILENCIO (sin
      // el spinner full-screen que se sentía como un reload al volver).
      const hadCache = menusCache && menusCache.uid === user.uid;
      if (!hadCache) setIsLoading(true);
      const [v1, v2] = await Promise.all([
        listFolder("menu", "v1", (name) => `/profile/${user.uid}/${name}`),
        listFolder("menu_v2", "v2", (name) => `/v2/editor/${name}`),
      ]);

      const all = [...v1, ...v2].sort((a, b) => {
        if (!a.updated) return 1;
        if (!b.updated) return -1;
        return new Date(b.updated) - new Date(a.updated);
      });

      menusCache = { uid: user.uid, data: all };
      if (isMounted) {
        setUserMenus(all);
        setIsLoading(false);
      }
    };

    fetchUserMenus();
    return () => {
      isMounted = false;
    };
  }, [user?.uid]);

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const localeMap = { es: "es-AR", en: "en-US", pt: "pt-BR" };
    return new Date(dateString).toLocaleDateString(localeMap[locale] || "es-AR", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };


  return (
    <div className="profile-screen">
      <Container className="profile-container">
        {isLoading ? (
          <LoadingSpinner />
        ) : userMenus.length === 0 ? (
          <div className="profile-empty">
            <h1 className="profile-title">{t("profile.sectionHeader")}</h1>
            <p className="profile-empty-text">{t("profile.emptyState")}</p>
          </div>
        ) : (
          <>
            <header className="profile-header">
              <h1 className="profile-title">{t("profile.sectionHeader")}</h1>
              <p className="profile-subtitle">
                {userMenus.length === 1
                  ? t("profile.subtitle.one")
                  : t("profile.subtitle.many").replace("{count}", userMenus.length)}
              </p>
            </header>

            <ul className="menu-list">
              {userMenus.map((menu) => (
                <li key={`${menu.version}-${menu.name}`}>
                  <Link to={menu.dashboardUrl} className="menu-list-item">
                    <div className="menu-list-item-body">
                      <h2 className="menu-list-item-title">{menu.title}</h2>
                      {menu.updated && (
                        <p className="menu-list-item-meta">
                          {t("profile.lastUpdate")} {formatDate(menu.updated)}
                        </p>
                      )}
                    </div>
                    <span className="menu-list-item-arrow">
                      <FaArrowRight />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}
      </Container>
    </div>
  );
};

export default Profile;
